import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fmtAsOf, fmtCell } from '@aibify/display/client';
import { api } from '../lib/api.ts';
import type { ArtifactSummary } from '../lib/contract.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { EmptyState, ErrorView, Loading } from '../components/Status.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, StatusLabel } from '../components/ui/badge.tsx';
import { IconChart, IconCheck, IconChevronForward, IconClock, IconFile, IconGrid, IconLayers, IconSearch, IconSparkles, IconX } from '../components/brand/icons.tsx';
import { Input, Select } from '../components/ui/input.tsx';
import { ShareChip } from '../components/ShareDrawer.tsx';
import { kindLabel, upgradeAvailable } from '../components/ArtifactKind.tsx';

export function ArtifactsPage() {
  const q = useQuery({ queryKey: ['artifacts'], queryFn: api.listArtifacts });
  // artifacts-09-archive: הודעה אחרי ארכוב מדף היצירה (state של הניווט; נעלמת בסגירה או בניווט)
  const loc = useLocation();
  const [notice, setNotice] = useState<string | null>(() => ((loc.state as { notice?: unknown } | null)?.notice as string | undefined) ?? null);
  const nav = useNavigate();
  useEffect(() => {
    // ה-state נקרא פעם אחת — רענון הדף לא מציג את ההודעה שוב
    if ((loc.state as { notice?: unknown } | null)?.notice) void nav(loc.pathname + loc.search, { replace: true, state: null });
  }, [loc, nav]);
  const [text, setText] = useState('');
  const [type, setType] = useState('');
  const [creator, setCreator] = useState('');
  // מיון לפי כותרת: לחיצה ראשונה — עולה (נכון ל- ועודכן — יורד: העדכני קודם), שנייה — הפוך.
  // artifacts-list-dates: ברירת המחדל — מה שהשתנה לאחרונה קודם
  const [sort, setSort] = useState<Sort>({ col: 'updated', dir: 'desc' });
  const toggleSort = (col: SortCol) => setSort((p) => (p.col === col ? { col, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'asOf' || col === 'updated' ? 'desc' : 'asc' }));
  const all = q.data?.artifacts ?? [];
  const creators = useMemo(() => [...new Map(all.map((a) => [a.creator.id, a.creator.nameHe])).entries()], [all]);
  const types = useMemo(() => [...new Set(all.map((a) => a.kind))], [all]);
  const needle = text.trim().toLowerCase();
  const list = all
    .filter(
      (a) =>
        (!needle || `${a.nameHe} ${a.descriptionHe} ${a.slug}`.toLowerCase().includes(needle)) &&
        (!type || a.kind === type) &&
        (!creator || a.creator.id === creator),
    )
    .sort((x, y) => compare(sort.col, x, y) * (sort.dir === 'asc' ? 1 : -1));

  const filtered = !!(needle || type || creator);

  return (
    <section className="flex flex-col gap-6">
      {notice && (
        <div role="status" className="fixed inset-x-4 bottom-[calc(1rem+var(--ab-foot))] z-40 mx-auto flex max-w-lg items-center gap-3 rounded-md border border-ok/40 bg-card p-3 shadow-pop" data-testid="toast">
          <IconCheck size={18} className="shrink-0 text-ok" />
          <span className="me-auto text-sm">{notice}</span>
          <button type="button" className="grid size-9 shrink-0 cursor-pointer place-items-center rounded-sm text-muted-foreground hover:bg-muted" aria-label={T.artifacts.dismiss} onClick={() => setNotice(null)}>
            <IconX size={16} />
          </button>
        </div>
      )}
      <header className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="page-title">{T.artifacts.title}</h1>
          <p className="m-0 max-w-3xl text-muted-foreground">{T.artifacts.subtitle}</p>
        </div>
        {q.data && (
          <span className="ms-auto text-sm text-muted-foreground" aria-live="polite">
            {T.artifacts.count(list.length, all.length)}
          </span>
        )}
      </header>

      <div className="grid grid-cols-1 gap-2 rounded-md border border-border bg-card p-2 shadow-card sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
        <div className="relative">
          <IconSearch size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input type="search" className="ps-10" placeholder={T.artifacts.search} aria-label={T.artifacts.search} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <Select aria-label="סוג" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">{T.artifacts.allTypes}</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {T.artifacts.kinds[t] ?? t}
            </option>
          ))}
        </Select>
        <Select aria-label="יוצר" value={creator} onChange={(e) => setCreator(e.target.value)}>
          <option value="">{T.artifacts.allCreators}</option>
          {creators.map(([id, n]) => (
            <option key={id} value={id}>
              {n}
            </option>
          ))}
        </Select>
        {filtered && (
          <Button
            variant="ghost"
            onClick={() => {
              setText('');
              setType('');
              setCreator('');
            }}
          >
            {T.artifacts.clear}
          </Button>
        )}
      </div>

      {q.isPending && <Loading variant="rows" />}
      {q.isError && <ErrorView error={q.error} onRetry={() => q.refetch()} />}
      {q.data && list.length === 0 && (
        <EmptyState icon={all.length ? <IconSearch size={22} /> : <IconGrid size={22} />} title={all.length ? T.artifacts.empty : T.artifacts.none} />
      )}
      {list.length > 0 && (
        // web-artifacts-rows-and-versions: טבלת שורות — גם במובייל (אותן שורות, דחוסות לשתי שורות; לא כרטיסים)
        <div className="overflow-x-auto rounded-md border border-border bg-card shadow-card max-md:overflow-visible">
          <table className="w-full border-collapse text-start max-md:block" data-testid="artifact-rows">
            <thead className="border-b border-border bg-muted/50 text-[13px] text-muted-foreground max-md:hidden">
              <tr>
                <SortTh col="name" sort={sort} onSort={toggleSort} label={T.artifacts.col.name} />
                <SortTh col="kind" sort={sort} onSort={toggleSort} label={T.artifacts.col.kind} />
                <th scope="col" className={TH}>
                  {T.artifacts.col.version}
                </th>
                <SortTh col="asOf" sort={sort} onSort={toggleSort} label={T.artifacts.col.asOf} />
                <SortTh col="updated" sort={sort} onSort={toggleSort} label={T.artifacts.col.updated} />
                <th scope="col" className={TH}>
                  {T.artifacts.col.creator}
                </th>
                <th scope="col" className={TH}>
                  {T.artifacts.col.share}
                </th>
                <th scope="col" className={TH}>
                  {T.artifacts.col.tags}
                </th>
                <th scope="col" className={TH}>
                  <span className="sr-only">{T.artifacts.col.open}</span>
                </th>
              </tr>
            </thead>
            <tbody className="max-md:block max-md:divide-y max-md:divide-border">
              {list.map((a) => (
                <Row key={a.slug} a={a} onOpen={(to) => void nav(to)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const TH = 'px-3 py-2 text-start font-semibold whitespace-nowrap';
const TD = 'px-3 py-2 align-middle';

/**
 * שורה ליצירה. דסקטופ: שם+תיאור / סוג / גרסה / נכון ל- / עודכן (+נוצר) / יוצר / שיתוף / תגים / פתח. מובייל (< md): אותה שורה כ-flex —
 * שורה 1: שם + תג סוג; שורה 2: נכון ל- · עודכן · יוצר · שיתוף · תגים (כל רכיב פעם אחת ב-DOM; הסדר ב-order).
 */
function Row({ a, onOpen }: { a: ArtifactSummary; onOpen: (to: string) => void }) {
  const TypeIcon = KIND_ICON[a.kind] ?? IconFile;
  const asOf = a.lastRun?.asOf ?? null;
  const to = `/artifacts/${a.slug}`;
  const up = upgradeAvailable(a);
  const tags = up || a.triggers.active > 0 || a.triggers.paused > 0;
  const updated = a.updatedAt ? fmtCell(a.updatedAt, 'date') : null;
  const created = a.createdAt ? fmtCell(a.createdAt, 'date') : null;
  return (
    <tr
      onClick={(e) => {
        // לחיצה על השורה = פתיחה; קישור בתוך השורה מטפל בעצמו
        if (!(e.target as HTMLElement).closest('a')) onOpen(to);
      }}
      className="cursor-pointer border-b border-border transition-colors last:border-b-0 hover:bg-muted/60 max-md:flex max-md:min-h-11 max-md:flex-wrap max-md:items-center max-md:gap-x-2 max-md:gap-y-1.5 max-md:border-b-0 max-md:px-3 max-md:py-2.5"
      data-testid={`artifact-row-${a.slug}`}
    >
      <td className={cn(TD, 'max-md:order-1 max-md:block max-md:min-w-0 max-md:flex-1 max-md:p-0')}>
        <span className="flex min-h-11 items-center gap-3 max-md:min-h-0">
          <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-md bg-primary-soft text-primary max-md:hidden">
            <TypeIcon size={18} />
          </span>
          <span className="flex min-w-0 flex-col">
            <Link to={to} className="truncate font-bold hover:text-primary" data-testid="row-name">
              {a.nameHe}
            </Link>
            <span className="line-clamp-1 text-[13px] text-muted-foreground max-md:hidden">{a.descriptionHe}</span>
          </span>
        </span>
      </td>
      <td className={cn(TD, 'whitespace-nowrap max-md:order-2 max-md:block max-md:p-0')}>
        <Badge tone="neutral" data-testid="row-kind">
          {kindLabel(a)}
        </Badge>
      </td>
      <td className={cn(TD, 'text-sm whitespace-nowrap tabular-nums max-md:hidden')} data-testid="row-version">
        {a.currentVersion != null ? T.artifact.version(a.currentVersion) : <span className="text-muted-foreground">{T.artifacts.draft}</span>}
      </td>
      {/* מובייל: שבירת שורה — מכאן השורה השנייה */}
      <td aria-hidden="true" className="hidden max-md:order-3 max-md:block max-md:h-0 max-md:basis-full max-md:p-0" />
      <td className={cn(TD, 'whitespace-nowrap max-md:order-4 max-md:block max-md:p-0')}>
        <Badge tone={asOf ? 'neutral' : 'warn'} data-testid="row-as-of" className="gap-1">
          <IconClock size={14} />
          {fmtAsOf(asOf)}
        </Badge>
      </td>
      {/* artifacts-list-dates: עודכן (ראשי) ונוצר (משני, מתחת; במובייל — רק עודכן, ונוצר ב-title) */}
      <td className={cn(TD, 'text-sm whitespace-nowrap tabular-nums max-md:order-5 max-md:block max-md:p-0 max-md:text-[13px] max-md:text-muted-foreground')} title={created ? T.artifacts.createdOn(created) : undefined} data-testid="row-updated">
        {updated && (
          <span className="flex flex-col">
            <span className="md:hidden">{T.artifacts.updatedOn(updated)}</span>
            <span className="max-md:hidden">{updated}</span>
            {created && (
              <span className="text-[12px] text-muted-foreground max-md:hidden" data-testid="row-created">
                {T.artifacts.createdOn(created)}
              </span>
            )}
          </span>
        )}
      </td>
      <td className={cn(TD, 'text-sm whitespace-nowrap text-muted-foreground max-md:order-6 max-md:block max-md:p-0 max-md:text-[13px]')} data-testid="row-creator">
        {a.creator.nameHe}
      </td>
      <td className={cn(TD, 'whitespace-nowrap max-md:order-7 max-md:block max-md:p-0')}>
        <ShareChip shared={a.shared} sharedBy={a.sharedBy} />
      </td>
      <td className={cn(TD, 'max-md:order-8 max-md:block max-md:p-0', !tags && 'max-md:hidden')}>
        <span className="flex flex-wrap items-center gap-1.5">
          {up && (
            <Badge tone="info" className="gap-1" data-testid="row-upgrade">
              <IconSparkles size={14} />
              {T.kinds.upgradeAvailable}
            </Badge>
          )}
          {a.triggers.active > 0 && (
            <Badge tone="info" className="gap-1" data-testid="row-scheduled">
              <IconClock size={14} />
              {T.triggers.scheduled}
            </Badge>
          )}
          {a.triggers.paused > 0 && (
            <StatusLabel tone="error" data-testid="row-paused">
              {T.triggers.paused}
            </StatusLabel>
          )}
        </span>
      </td>
      <td className={cn(TD, 'max-md:hidden')}>
        {/* "פתח" — אותו יעד כמו השם (הקישור הנגיש הוא השם; זה כפול לעכבר) */}
        <Link to={to} tabIndex={-1} aria-hidden="true" className="inline-flex min-h-11 items-center gap-1 text-sm font-medium whitespace-nowrap text-primary hover:underline" data-testid="row-open">
          {T.artifacts.open}
          <IconChevronForward size={16} />
        </Link>
      </td>
    </tr>
  );
}

type SortCol = 'name' | 'kind' | 'asOf' | 'updated';
type Sort = { col: SortCol; dir: 'asc' | 'desc' };

function SortTh({ col, sort, onSort, label }: { col: SortCol; sort: Sort; onSort: (c: SortCol) => void; label: string }) {
  const on = sort.col === col;
  return (
    <th scope="col" aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} className="px-1 py-1 text-start font-semibold whitespace-nowrap">
      <button
        type="button"
        onClick={() => onSort(col)}
        className={cn('inline-flex min-h-9 cursor-pointer items-center gap-1 rounded-sm px-2 hover:bg-muted hover:text-foreground', on && 'text-foreground')}
        title={T.artifacts.sortBy(label)}
        data-testid={`sort-${col}`}
      >
        {label}
        <span aria-hidden="true" className={cn('text-[10px]', !on && 'opacity-0')}>
          {sort.dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

const asOfMs = (a: ArtifactSummary) => (a.lastRun?.asOf ? Date.parse(a.lastRun.asOf) : -Infinity);
const updatedMs = (a: ArtifactSummary) => (a.updatedAt ? Date.parse(a.updatedAt) : -Infinity);
const kindHe = (a: ArtifactSummary) => T.artifacts.kinds[a.kind] ?? a.kind;
function compare(col: SortCol, x: ArtifactSummary, y: ArtifactSummary): number {
  const byName = x.nameHe.localeCompare(y.nameHe, 'he');
  if (col === 'asOf') return asOfMs(x) - asOfMs(y) || byName;
  if (col === 'updated') return updatedMs(x) - updatedMs(y) || byName;
  if (col === 'kind') return kindHe(x).localeCompare(kindHe(y), 'he') || byName;
  return byName;
}

const KIND_ICON: Record<string, typeof IconFile> = { report: IconChart, chat: IconSparkles, app: IconLayers };
